import { useAppContext } from "@contexts";
import { DropdownMenu } from "@controls";
import { useOrganizationContext } from "@src/modules/member/context/organization-context/hook";
import { useLogout, useMe } from "@src/modules/member/hooks/me";
import {
  useOrganization,
  useOrganizations,
} from "@src/modules/member/hooks/organizations";
import type { Organization } from "@src/modules/member/models/organization";
import {
  INVOICE_SETTINGS_PATH,
  NEW_ORGANIZATION_PATH,
} from "@src/modules/member/modules/settings/router/paths";
import {
  ORGANIZATION_PATH,
  PROFILE_PATH,
} from "@src/modules/member/modules/settings/router/paths";
import classNames from "classnames";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

function OrganizationImage({ organization }: { organization?: Organization }) {
  if (!organization?.name) {
    return null;
  }

  if (organization.image_url) {
    return (
      <img
        src={organization.image_url}
        alt={organization.name}
        className="w-10 rounded"
      />
    );
  }

  return <div>{organization.name[0].toUpperCase()}</div>;
}

interface OrganizationProps {
  minimized: boolean;
}

function Organization({ minimized }: OrganizationProps) {
  const { t } = useTranslation();

  const { setSwitchOrganizationDialogIsOpen } = useAppContext();
  const { currentOrganizationCode } = useOrganizationContext();
  const navigate = useNavigate();
  const {
    data: organizationsResponse,
    isLoading: organizationsIsLoading,
    dataUpdatedAt: organizationsUpdatedAt,
  } = useOrganizations();
  const { data: organizationResponse } = useOrganization(
    currentOrganizationCode as string,
  );

  useEffect(() => {
    if (!organizationsIsLoading && organizationsResponse?.data?.length === 0) {
      navigate(NEW_ORGANIZATION_PATH());
    }
  }, [organizationsUpdatedAt]);

  const { mutate: logout } = useLogout();

  const { data: meResponse, isLoading: meIsLoading } = useMe();
  const me = meResponse?.data ?? { email: "" };

  const organization = organizationResponse?.data;

  const cantDisplayOrganizations = !organization || meIsLoading;
  if (cantDisplayOrganizations) {
    return (
      <div
        className={classNames(
          "border-gray-200 bg-white rounded-lg w-full h-fit p-2 flex gap-2",
        )}
      >
        <div className="h-10 w-10 bg-gray-200 rounded" />
        {!minimized && (
          <div className="flex-grow flex flex-col gap-1">
            <div className="h-4 w-full bg-gray-200 rounded" />
            <div className="h-[12px] w-2/3 bg-gray-200 rounded" />
          </div>
        )}
      </div>
    );
  }

  const handleLogout = () => {
    logout(null);
  };

  const menuActions = [
    {
      key: "settings",
      actions: [
        {
          key: "organization",
          label: t("organizationSettings"),
          onClick: () => navigate(ORGANIZATION_PATH()),
        },
        {
          key: "profile",
          label: t("profileSettings"),
          onClick: () => navigate(PROFILE_PATH()),
        },
        {
          key: "profile",
          label: t("member.settings.invoices.invoiceSettings"),
          onClick: () => navigate(INVOICE_SETTINGS_PATH()),
        },
      ],
    },
    {
      key: "others",
      actions: [
        {
          key: "switchOrganization",
          label: t("switchOrganization"),
          onClick: () => setSwitchOrganizationDialogIsOpen(true),
        },
        {
          key: "logout",
          label: t("logout"),
          onClick: handleLogout,
        },
      ],
    },
  ];

  return (
    <DropdownMenu
      triggerChild={
        <div
          className={classNames(
            "border-gray-200 bg-white rounded-lg",
            "p-2 flex",
            "w-full h-fit",
          )}
        >
          <div className="h-10 min-w-10 bg-white rounded flex justify-center items-center text-lg font-bold">
            <OrganizationImage organization={organization} />
          </div>
          {!minimized && (
            <div className="text-gray-900 flex flex-grow flex-col justify-center min-w-0 max-w-full text-start ml-2">
              <div className="text-sm overflow-hidden whitespace-nowrap font-semibold my-0">
                {organization.name}
              </div>
              <span className="my-0 font-light text-xs">{me.email}</span>
            </div>
          )}
        </div>
      }
      ariaLabel="change organization"
      groups={menuActions}
    />
  );
}

export default Organization;
